import { useState, useCallback } from "react";
import {
  getWinnerInfo,
  submitWinnerInfo,
  updateWinnerInfo,
} from "../../store/api/newyearApi";
import { WINNER_INFO_STATUS, MAX_MODIFICATIONS } from "../../constants/newyearConstants";

const useWinnerInfo = (eventId) => {
  const [winnerInfo, setWinnerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchWinnerInfo = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getWinnerInfo(eventId);
      setWinnerInfo(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const submit = useCallback(
    async (name, phone) => {
      if (!eventId) return;

      setSubmitting(true);
      setError(null);

      try {
        const data = await submitWinnerInfo(eventId, name, phone);
        setWinnerInfo(data);
        return data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [eventId]
  );

  const update = useCallback(
    async (name, phone, reason) => {
      if (!eventId) return;

      setSubmitting(true);
      setError(null);

      try {
        const data = await updateWinnerInfo(eventId, name, phone, reason);
        setWinnerInfo(data);
        return data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [eventId]
  );

  const canModify = useCallback(() => {
    if (!winnerInfo) return false;
    if (winnerInfo.status === WINNER_INFO_STATUS.EXPIRED) return false;
    return (winnerInfo.modificationCount ?? 0) < MAX_MODIFICATIONS;
  }, [winnerInfo]);

  const getRemainingModifications = useCallback(() => {
    if (!winnerInfo) return MAX_MODIFICATIONS;
    return Math.max(0, MAX_MODIFICATIONS - (winnerInfo.modificationCount ?? 0));
  }, [winnerInfo]);

  const isExpired = useCallback(() => {
    return winnerInfo?.status === WINNER_INFO_STATUS.EXPIRED;
  }, [winnerInfo]);

  const isSubmitted = useCallback(() => {
    return winnerInfo?.status === WINNER_INFO_STATUS.SUBMITTED;
  }, [winnerInfo]);

  const isPendingSubmission = useCallback(() => {
    return winnerInfo?.status === WINNER_INFO_STATUS.PENDING_SUBMISSION;
  }, [winnerInfo]);

  return {
    winnerInfo,
    loading,
    submitting,
    error,
    fetchWinnerInfo,
    submit,
    update,
    canModify,
    getRemainingModifications,
    isExpired,
    isSubmitted,
    isPendingSubmission,
  };
};

export default useWinnerInfo;
